import React, { useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import SampleOriginCard from "./SampleOriginCard";

export const MONO_FONT = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** "7b02f1c4": the first block of a sample UUID, enough to tell picks apart. */
export const shortSampleId = (sampleId) => String(sampleId || "").split("-")[0];

/** "52.3 / 12.0 / -8.4", or a dash when no colour was measured. */
export const formatLab = (lab) =>
    Array.isArray(lab) ? lab.map((value) => Number(value).toFixed(1)).join(" / ") : "—";

/** "Q2 · B0 · Row 3 · Col 5": where the sample was marked (generation,
 *  batch, position in the patch group - as the plate tiles and the patch
 *  tooltips name them), or null when unknown. */
export function markedInLabel(markedIn) {
    if (!markedIn) return null;
    const parts = [];
    if (markedIn.generation) parts.push(markedIn.generation);
    if (markedIn.batch !== null && markedIn.batch !== undefined) parts.push(`B${markedIn.batch}`);
    if (markedIn.row !== null && markedIn.row !== undefined) {
        parts.push(`Row ${markedIn.row} · Col ${markedIn.column}`);
    }
    return parts.length ? parts.join(" · ") : null;
}

/** "Plate 0 · SS-AISI304-…-CHROMA": plate and exploration of a sample. */
export function plateAndExploration(row) {
    return [row?.marked_in?.plate, row?.exploration_name].filter(Boolean).join(" · ") || "—";
}

/** Measured colour of a sample as a square swatch (grey when unmeasured). */
export function Swatch({ rgb, size = 28 }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: 0.75,
                flexShrink: 0,
                bgcolor: Array.isArray(rgb) ? `rgb(${rgb.join(",")})` : "#e5e7eb",
                border: "1px solid rgba(15, 23, 42, 0.2)",
            }}
        />
    );
}

/**
 * The manual selection's palette beside the picking pages: every picked
 * sample with its colour, ID, where it was marked, L*a*b* and exploration
 * (the user holds the marked plates and checks picks against them), a
 * remove button each and Clear for the whole palette. Clicking a sample
 * opens its origin card (design and performance space).
 *
 * Props:
 *   selectionId          for the origin lookup
 *   picks                rows of api/selection/manual/samples, in pick order
 *   loading              the first load is still running
 *   onRemove(sampleId)   remove one sample
 *   onClear()            remove every sample (confirmed here first)
 */
export default function ManualPicksPanel({ selectionId, picks, loading, onRemove, onClear }) {
    const [confirmClear, setConfirmClear] = useState(false);
    const [originOpen, setOriginOpen] = useState(false);
    const [origin, setOrigin] = useState(null);
    const [originLoading, setOriginLoading] = useState(false);
    const [originError, setOriginError] = useState(null);

    const inspect = async (sampleId) => {
        setOriginOpen(true);
        setOrigin(null);
        setOriginError(null);
        setOriginLoading(true);
        try {
            const response = await axios.post("api/selection/sample/origin", { selectionId, sampleId });
            setOrigin(response.data.origin);
        } catch (err) {
            setOriginError(err?.response?.data?.error || "Loading the sample failed.");
        } finally {
            setOriginLoading(false);
        }
    };

    const count = picks.length;

    return (
        <Card
            variant="outlined"
            aria-label="Palette"
            sx={{
                borderRadius: 3,
                borderColor: "rgba(15, 23, 42, 0.12)",
                position: { lg: "sticky" },
                top: { lg: 16 },
            }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Palette
                    </Typography>
                    <Typography variant="body2" color="text.secondary" data-testid="palette-count">
                        {`${count} sample${count === 1 ? "" : "s"}`}
                    </Typography>
                </Stack>

                {loading ? (
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} thickness={5} />
                        <Typography variant="body2" color="text.secondary">Loading the palette…</Typography>
                    </Stack>
                ) : count === 0 ? (
                    <Box
                        sx={{
                            py: 3,
                            px: 1.5,
                            textAlign: "center",
                            border: "1px dashed rgba(15, 23, 42, 0.2)",
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No samples picked.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        component="ol"
                        sx={{
                            listStyle: "none",
                            p: 0,
                            m: 0,
                            maxHeight: { lg: "62vh" },
                            overflowY: "auto",
                        }}
                    >
                        {picks.map((pick, index) => (
                            <Box
                                component="li"
                                key={pick.sample_id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    py: 0.75,
                                    pl: 0.5,
                                    pr: 0.25,
                                    borderRadius: 1.5,
                                    "&:hover": { bgcolor: "rgba(15, 23, 42, 0.04)" },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ width: 18, textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
                                >
                                    {index + 1}
                                </Typography>
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Inspect sample ${pick.sample_id}`}
                                    onClick={() => inspect(pick.sample_id)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") inspect(pick.sample_id);
                                    }}
                                    sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0, cursor: "pointer" }}
                                >
                                    <Swatch rgb={pick.rgb} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Tooltip title={pick.sample_id} placement="left">
                                            <Typography sx={{ fontFamily: MONO_FONT, fontSize: 13, fontWeight: 700, lineHeight: 1.35 }}>
                                                {shortSampleId(pick.sample_id)}
                                            </Typography>
                                        </Tooltip>
                                        {markedInLabel(pick.marked_in) ? (
                                            <Typography
                                                variant="caption"
                                                sx={{ display: "block", lineHeight: 1.3, fontWeight: 600, color: "text.primary" }}
                                            >
                                                {markedInLabel(pick.marked_in)}
                                            </Typography>
                                        ) : null}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}
                                        >
                                            {`L*a*b* ${formatLab(pick.lab)}`}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", lineHeight: 1.3, wordBreak: "break-word" }}
                                        >
                                            {plateAndExploration(pick)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${pick.sample_id}`}
                                    onClick={() => onRemove(pick.sample_id)}
                                    sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}

                {count > 0 ? (
                    <Button
                        size="small"
                        onClick={() => setConfirmClear(true)}
                        sx={{ mt: 1, textTransform: "none", color: "text.secondary", fontWeight: 600 }}
                    >
                        Clear
                    </Button>
                ) : null}
            </CardContent>

            <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
                <DialogTitle>Clear the palette?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`All ${count} picked sample${count === 1 ? " is" : "s are"} removed.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disableElevation
                        onClick={() => {
                            setConfirmClear(false);
                            onClear();
                        }}
                    >
                        Clear
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={originOpen} onClose={() => setOriginOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>Sample</DialogTitle>
                <DialogContent>
                    <SampleOriginCard origin={origin} loading={originLoading} error={originError} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOriginOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
